import { InsuranceSubscriberRelationship } from 'domains/client';
import { PayerTypes } from 'domains/signup/data/types';
import { CommonFieldsSchema, getAgeValidation } from 'domains/signupDirect/schemas/common';
import { PaymentMethod } from 'domains/signupDirect/schemas/filtersSchema';
import { EligibilityCheckResult } from 'domains/signupv4/Insurance/types';
import { isUndefined } from 'lodash';
import { TypeInsuranceFields } from 'types/contentful';
import { z } from 'zod';
import { EvaluationScheduleSchema } from './evaluationScheduleSchema';
import { RequiredErrorMessage } from 'utils/validators/zodHelpers';
import { PCPInfoSchema, requiredPCPSchema } from './PCPSchema';
import { EmailSchema, PasswordSchema } from 'utils/validators/commonSchemas';

const InsuranceInfo = z.object({
  insuranceData: PaymentMethod.options[0].pick({
    insuranceData: true,
  }).shape.insuranceData,
  memberId: z.string(RequiredErrorMessage).min(1, RequiredErrorMessage),
  groupId: z.string(RequiredErrorMessage).optional(),
  eligibilityCheckResult: z.nativeEnum(EligibilityCheckResult).optional(),
  preApprovedEligibilityCheck: z.boolean().optional(),
});

const PrimaryInsuranceSubscriber = z
  .object({
    relationShipToClient: z.nativeEnum(InsuranceSubscriberRelationship, RequiredErrorMessage),
    dateOfBirth: getAgeValidation(true),
    firstName: z.string(RequiredErrorMessage).optional(),
    lastName: z.string(RequiredErrorMessage).optional(),
  })
  .refine(
    data => {
      const isSelf = data.relationShipToClient === InsuranceSubscriberRelationship.Self;
      const isValidDOB = !isUndefined(data.dateOfBirth);
      return isSelf ? true : isValidDOB;
    },
    {
      message: RequiredErrorMessage.message,
      path: ['dateOfBirth'],
    },
  )
  .refine(data => (data.relationShipToClient !== InsuranceSubscriberRelationship.Self ? data.firstName : true), {
    message: RequiredErrorMessage.message,
    path: ['firstName'],
  })
  .refine(data => (data.relationShipToClient !== InsuranceSubscriberRelationship.Self ? data.lastName : true), {
    message: RequiredErrorMessage.message,
    path: ['lastName'],
  });

export type PrimaryInsuranceSubscriberType = Required<z.infer<typeof PrimaryInsuranceSubscriber>>;

// Shared CC info
const CCPaymentInfo = z.object({
  stripeMethodId: z.string(RequiredErrorMessage).min(1, RequiredErrorMessage),
});

// Medicaid
export const MedicaidBilling = InsuranceInfo.extend({
  type: z.literal('insurance-medicaid'),
});

// Commercial Insurance
export const CommercialInsuranceBilling = InsuranceInfo.extend({
  type: z.literal('insurance-commercial'),
  stripeMethodId: CCPaymentInfo.shape.stripeMethodId.optional(),
  subscriber: PrimaryInsuranceSubscriber,
});

// Medicare
export const MedicareBilling = InsuranceInfo.extend({
  type: z.literal('insurance-medicare'),
  stripeMethodId: CCPaymentInfo.shape.stripeMethodId.optional(),
});

// Private Pay billing
export const PrivatePayBilling = CCPaymentInfo.extend({
  type: z.literal('privatePay'),
});

export const BillingInfo = z
  .discriminatedUnion('type', [PrivatePayBilling, MedicaidBilling, MedicareBilling, CommercialInsuranceBilling])
  .refine(
    data => {
      // Note(juan): this needs to be done here sadly, after the discriminated union merge
      if (data.type === 'insurance-commercial') {
        if (data.eligibilityCheckResult === EligibilityCheckResult.IN_NETWORK || data.preApprovedEligibilityCheck) {
          return data.stripeMethodId;
        }

        if (data.eligibilityCheckResult === EligibilityCheckResult.OUT_OF_NETWORK) {
          return data.stripeMethodId;
        }
        return true;
      } else if (data.type === 'insurance-medicare') {
        // Medicare always requires credit card collection (patient responsibility with $0 deposit)
        if (data.eligibilityCheckResult === EligibilityCheckResult.IN_NETWORK || data.preApprovedEligibilityCheck) {
          return data.stripeMethodId;
        }
        return true;
      } else {
        return true;
      }
    },
    {
      message: RequiredErrorMessage.message,
      path: ['stripeMethodId'],
    },
  );

const ExistingContactInfo = z.object({
  isAlreadyContact: z.literal(true),
  contactId: z.string(),
  email: EmailSchema,
});

const NewContactInfo = z.object({
  isAlreadyContact: z.literal(false),
  email: EmailSchema,
  newPassword: PasswordSchema,
});

export const LoginContactInfo = z.discriminatedUnion('isAlreadyContact', [ExistingContactInfo, NewContactInfo]);
export type LoginContactInfoType = z.infer<typeof LoginContactInfo>;

// Form schemas
export const EvaluationForm = CommonFieldsSchema.extend({
  loginContactInfo: LoginContactInfo,
  evaluationSchedule: EvaluationScheduleSchema,
  billingInfo: BillingInfo,
  leadId: z.string().optional(),
  insuranceLeadId: z.string().optional(),
  consentToTerms: z.boolean().optional(),
  primaryPhysician: PCPInfoSchema,
}).superRefine((data, ctx) => {
  // Skip PCP validation if not required
  if (!data.primaryPhysician.physicianOrderRequired) return;

  const insuranceType = data.billingInfo.type;

  // Skip validation for private pay
  if (insuranceType === 'privatePay') return;

  // For Medicaid: Allow eligibility to run first, then validate PCP only for actual eligibility results
  if (insuranceType === 'insurance-medicaid') {
    const eligibilityCheckResult = data.billingInfo.eligibilityCheckResult;

    // Skip validation until eligibility check is attempted
    if (eligibilityCheckResult === undefined) return;

    // Only validate PCP for actual eligibility results, not request failures
    const shouldValidatePCP =
      eligibilityCheckResult === EligibilityCheckResult.IN_NETWORK ||
      eligibilityCheckResult === EligibilityCheckResult.OUT_OF_NETWORK ||
      eligibilityCheckResult === EligibilityCheckResult.INACTIVE;

    if (shouldValidatePCP) {
      const result = requiredPCPSchema.safeParse(data.primaryPhysician);

      if (!result.success) {
        for (const error of result.error.errors) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: RequiredErrorMessage.message,
            path: ['primaryPhysician', ...error.path],
          });
        }
      }
    }
    return;
  }

  // For Medicare and Commercial: Validate PCP fields immediately if required by the payer
  if (insuranceType === 'insurance-medicare' || insuranceType === 'insurance-commercial') {
    // Use safeParse to validate required PCP fields (Juan's suggestion)
    const result = requiredPCPSchema.safeParse(data.primaryPhysician);

    if (!result.success) {
      for (const error of result.error.errors) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: RequiredErrorMessage.message,
          path: ['primaryPhysician', ...error.path],
        });
      }
    }
    return;
  }
});

export type EvaluationFormType = z.infer<typeof EvaluationForm>;

export const paymentMethodIsInsurance = (paymentData: EvaluationFormType['billingInfo']) => {
  return (
    paymentData.type === 'insurance-commercial' ||
    paymentData.type === 'insurance-medicaid' ||
    paymentData.type === 'insurance-medicare'
  );
};

export const paymentTypeIsInsurance = (type: EvaluationFormType['billingInfo']['type']) => {
  return type === 'insurance-commercial' || type === 'insurance-medicaid' || type === 'insurance-medicare';
};

// TODO: find a good spot to move this
export const shouldHideContinueButtonForEligibility = (eligibilityResult: EligibilityCheckResult | undefined) => {
  return (
    eligibilityResult === undefined ||
    eligibilityResult === EligibilityCheckResult.ERROR ||
    eligibilityResult === EligibilityCheckResult.INCORRECT_MEMBER_ID ||
    eligibilityResult === EligibilityCheckResult.INCORRECT_NAME ||
    eligibilityResult === EligibilityCheckResult.INCORRECT_DOB ||
    eligibilityResult === EligibilityCheckResult.INVALID_PARTICIPANT_ID
  );
};

export const getInsuranceSchemaType = (insuranceType: TypeInsuranceFields['insuranceType']) => {
  switch (insuranceType) {
    case 'Commercial':
      return 'insurance-commercial';
    case 'Medicaid':
      return 'insurance-medicaid';
    case 'Medicare':
      return 'insurance-medicare';
  }
};

export const getPayerTypeFromBillingInfo = (info: EvaluationFormType['billingInfo']): PayerTypes => {
  switch (info.type) {
    case 'insurance-commercial':
      return PayerTypes.COMMERCIAL;
    case 'insurance-medicaid':
      return PayerTypes.MEDICAID;
    case 'insurance-medicare':
      return PayerTypes.MEDICARE;
    case 'privatePay':
      throw `Couldn't map billing info to payer type, info type was ${info.type}`;
  }
};
